import React, { useState, useCallback, useEffect, useRef } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Container } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Header from "../components/impact/Header";
import StatsOverview from "../components/impact/StatsOverview";
import AgeDistribution from "../components/impact/AgeDistribution";
import AgenciesEngaged from "../components/impact/AgenciesEngaged";
import AgencyCoverage from "../components/impact/AgencyCoverage";
import InterviewsConducted from "../components/impact/InterviewsConducted";
import VideosUploaded from "../components/impact/VideosUploaded";
import { states, mapApiToDashboardData } from "../data/impactData";
import { FilterState, DashboardData } from "../types";
import { ImpactApi } from "../utils/api";
import QualifiedSavings from "../components/impact/QualifiedSavings";
import ExportImpact from "./ExportImpact";
import { useParams, useSearchParams } from "react-router-dom";

const theme = createTheme({
  palette: {
    primary: {
      main: "#592b7b",
      light: "#8B5CF6",
      dark: "#553C9A",
    },
    secondary: {
      main: "#3B82F6",
      light: "#60A5FA",
      dark: "#2563EB",
    },
    background: {
      default: "#F8FAFC",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1E293B",
      secondary: "#64748B",
    },
  },
  typography: {
    fontFamily: '"Lato"',
    h2: {
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.2,
    },
    h3: {
      fontWeight: 700,
      fontSize: "1.25rem",
      lineHeight: 1.2,
    },
    h4: {
      fontWeight: 700,
      fontSize: "1.25rem",
      lineHeight: 1.2,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.3,
    },
    h6: {
      fontWeight: 600,
      fontSize: "0.875rem",
      lineHeight: 1.4,
    },
    body1: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.75rem",
      lineHeight: 1.4,
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: "175px !important",
          paddingRight: "175px !important",
          "@media (max-width: 1280px)": {
            paddingLeft: "100px !important",
            paddingRight: "100px !important",
          },
          "@media (max-width: 900px)": {
            paddingLeft: "50px !important",
            paddingRight: "50px !important",
          },
          "@media (max-width: 600px)": {
            paddingLeft: "12px !important",
            paddingRight: "12px !important",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          border: "1px solid #E2E8F0",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
  },
});

function App() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  // ?state=XX (case-insensitive). Only used when no centerId is in the path —
  // a centerId is more specific and wins. Invalid codes silently fall back to
  // the nationwide view.
  const stateQueryParam = (searchParams.get("state") || "").toLowerCase();
  const initialStateValue =
    !id && stateQueryParam && states.some((s) => s.value === `us-${stateQueryParam}`)
      ? `us-${stateQueryParam}`
      : "";
  const initialStateName = initialStateValue
    ? states.find((s) => s.value === initialStateValue)?.name || ""
    : "";

  const hasLoaded = useRef(false);
  const [mapState, setMapState] = React.useState(initialStateValue || "All States");
  const [stateOrOrganization, setStateOrOrganization] = useState(initialStateName);
  const [isExportMode, setIsExportMode] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [centerState, setCenterState] = useState<string>(initialStateValue);
  const [centerName, setCenterName] = useState<string>("");
  const [filters, setFilters] = useState<FilterState>({
    state: initialStateValue || "All States",
    showOrgData: false,
  });
  const [useVerbiage, setUseVerbiage] = useState<
    "state" | "organization" | "nationwide"
  >(initialStateValue ? "state" : "nationwide");

  async function loadDashboardData() {
    setLoading(true);
    setError(null);
    const stateForApi = initialStateValue
      ? initialStateValue.slice(-2).toUpperCase()
      : undefined;
    Promise.all([
      ImpactApi.getImpactReportData(stateForApi),
      ImpactApi.getLocationData(stateForApi),
    ])
      .then(([impact, locations]) => {
        setDashboardData(
          mapApiToDashboardData(
            (impact as any).impactReport as Record<string, unknown>[],
            locations,
            id,
            (impact as any).agenciesCount
          )
        );
      })
      .catch((error) => {
        console.log(error);
        setError("Failed to load impact data");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (dashboardData?.centerName && dashboardData.centerName !== "") {
      setCenterName(dashboardData.centerName);
    }
    if (dashboardData?.state) {
      const formattedState = `us-${dashboardData.state.toLowerCase()}`;
      setCenterState(formattedState);
    }
  }, [dashboardData]);

  function updateDashboardData(state?: string, centerId?: string) {
    setLoading(true);
    setError(null);
    Promise.all([
      ImpactApi.getImpactReportData(state, centerId),
      ImpactApi.getLocationData(state, centerId),
    ])
      .then(([impact, locations]) => {
        setDashboardData((prev) => {
          if (!prev) return null;
          const filteredData = mapApiToDashboardData(
            (impact as any).impactReport as Record<string, unknown>[],
            locations,
            id,
            (impact as any).agenciesCount
          );
          return filteredData;
        });
      })
      .catch((error) => {
        console.log(error);
        setError("Failed to load impact data");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!hasLoaded.current) {
      loadDashboardData();
      hasLoaded.current = true;
    }
  }, []);

  const handleExportPDF = () => {
    setIsExporting(true);
    setIsExportMode(true);
  };

  const handleFilterChange = useCallback(
    (newFilters: Partial<FilterState>) => {
      if (newFilters.state && !newFilters.showOrgData) {
        if (newFilters.state === "us-pr") {
          setUseVerbiage("state");
          setStateOrOrganization("Puerto Rico");
        } else {
          const state =
            states.find((state) => state.value === newFilters.state)?.name ||
            "";
          setStateOrOrganization(state);
          setUseVerbiage("state");
        }
        setMapState(newFilters.state);
        if (newFilters.state === "All States") {
          setUseVerbiage("nationwide");
          updateDashboardData();
        } else {
          updateDashboardData(
            newFilters.state.slice(-2).toUpperCase(),
            undefined
          );
        }
      } else if (!newFilters.showOrgData) {
        setUseVerbiage("nationwide");
        setStateOrOrganization("");
        setMapState("All States");
        updateDashboardData();
      } else {
        let state = newFilters.state || "";
        setUseVerbiage("organization");
        setStateOrOrganization(centerName);
        setMapState(state || "All States");
        updateDashboardData(state.slice(-2).toUpperCase(), id);
      }
      setFilters((prev) => ({ ...prev, ...newFilters }));
    },
    [centerName]
  );

  const handleRefresh = useCallback(() => {
    // When the page is locked to a URL state, refresh re-loads that state
    // instead of clearing the filter (the dropdown is hidden so there's no
    // way back).
    setFilters({
      state: initialStateValue || "All States",
      showOrgData: false,
    });
    setMapState(initialStateValue || "All States");
    loadDashboardData();
  }, [initialStateValue]);

  // Replace getCurrentData and currentData with dashboardData

  return (
    <>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1000,
            fontFamily: "Lato",
          }}
        >
          {isExportMode ? (
            <ExportImpact
              mapState={mapState}
              filters={filters}
              useVerbiage={useVerbiage}
              stateOrOrganization={stateOrOrganization}
              setLoading={setIsExporting}
              setIsExportMode={setIsExportMode}
              dashboardData={dashboardData || ({} as DashboardData)}
              centerName={centerName}
            />
          ) : null}
        </Box>
        <Box
          id="page-content"
          sx={{
            minHeight: "100vh",
            bgcolor: "background.default",
            background: "#f3f3f3",
          }}
        >
          <Container maxWidth="xl" sx={{ py: 3 }}>
            <Header
              filters={filters}
              onFilterChange={handleFilterChange}
              onRefresh={handleRefresh}
              organization={centerName}
              stateOrOrganization={stateOrOrganization}
              onExportPDF={handleExportPDF}
              isLoading={loading}
              isExporting={isExporting}
              centerState={centerState}
              hideStateFilter={!!initialStateValue}
            />

            {loading ? (
              <Box sx={{ mt: 4, textAlign: "center" }}>
                <CircularProgress size={48} thickness={4} color="primary" />
              </Box>
            ) : error ? (
              <Box sx={{ mt: 4, textAlign: "center", color: "red" }}>
                {error}
              </Box>
            ) : dashboardData ? (
              <Box
                sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 3 }}
              >
                <StatsOverview data={dashboardData.stats} />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 3,
                  }}
                >
                  <AgeDistribution
                    data={dashboardData.ageDistribution}
                    useVerbiage={useVerbiage}
                    stateOrOrganization={stateOrOrganization}
                  />
                  <InterviewsConducted
                    data={dashboardData.interviewsConducted}
                    useVerbiage={useVerbiage}
                    stateOrOrganization={stateOrOrganization}
                  />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 3,
                  }}
                >
                  <VideosUploaded
                    data={dashboardData.videosUploaded}
                    useVerbiage={useVerbiage}
                    stateOrOrganization={stateOrOrganization}
                  />
                  <AgenciesEngaged data={dashboardData.agenciesEngaged} />
                </Box>

                <AgencyCoverage
                  data={dashboardData.agencyCoverage}
                  setMapState={setMapState}
                  mapState={mapState}
                  hideFilter={filters.showOrgData}
                />

                <QualifiedSavings
                  data={dashboardData.qualifiedSavings}
                  useVerbiage={useVerbiage}
                  stateOrOrganization={stateOrOrganization}
                />
              </Box>
            ) : null}
          </Container>
        </Box>
      </ThemeProvider>
    </>
  );
}

export default App;
